import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Loader2, KeyRound, Archive, ArchiveRestore, Mail } from "lucide-react";

export function DealerUsersTab({
  dealerId,
  users,
  showArchived,
  onToggleArchived,
  onChanged,
  locations = [],
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [userForm, setUserForm] = useState({ name: "", email: "", password: "", dealer_role: "DEALER_ADMIN", job_title: "", location_id: "" });
  const [addingUser, setAddingUser] = useState(false);
  const [resendTarget, setResendTarget] = useState(null);
  const [resending, setResending] = useState(false);
  const [scopeTarget, setScopeTarget] = useState(null);
  const [scopeForm, setScopeForm] = useState({ dealer_role: "DEALER_ADMIN", location_id: "", job_title: "" });
  const [savingScope, setSavingScope] = useState(false);
  const [continuity, setContinuity] = useState(null); // {userId, detail} — deactivation of a project owner
  const [continuityChoice, setContinuityChoice] = useState("UNASSIGNED");

  const locName = (id) => {
    const l = locations.find((x) => x.id === id);
    return l ? l.label || l.type : "";
  };
  const ROLE_LABEL = { DEALER_ADMIN: "Dealer Admin", LOCATION_MANAGER: "Location Manager", SALES_DESIGNER: "Sales / Designer" };
  const effectiveRole = (u) => u.dealer_role || (u.location_scope === "location" ? "LOCATION_MANAGER" : "DEALER_ADMIN");
  const accessLabel = (u) => {
    const r = effectiveRole(u);
    const base = r === "DEALER_ADMIN" ? "Dealer Admin — all locations" : `${ROLE_LABEL[r]} — ${locName(u.location_id) || "unassigned"}`;
    return u.job_title ? `${base} · ${u.job_title}` : base;
  };
  // First-user rule: a dealer with no usable users gets a Dealer Admin, always
  const hasUsableUsers = users.some((u) => !u.archived);
  const formRole = hasUsableUsers ? userForm.dealer_role : "DEALER_ADMIN";
  // §18: business-readable lifecycle — Invited (setup pending) / Active / Deactivated / Archived
  const lifecycle = (u) =>
    u.archived ? "archived" : !u.active ? "deactivated" : u.force_password_change ? "invited" : "active";
  const LIFECYCLE_LABEL = { invited: "Invited — setup pending", active: "Active", deactivated: "Deactivated", archived: "Archived" };
  const LIFECYCLE_CLS = { invited: "badge-reserved", active: "badge-available", deactivated: "badge-neutral", archived: "badge-neutral" };

  const fmtWhen = (iso) => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  // onboarding state is computed server-side; SENT means the provider accepted the message
  const INVITE_LABEL = {
    pending: () => "Invitation pending — sending…",
    sent_awaiting_setup: (i) => `Invitation sent ${i.sent_at ? fmtWhen(i.sent_at) + " " : ""}— awaiting setup · expires ${i.expires_at ? fmtWhen(i.expires_at) : "—"}`,
    failed: () => "Invitation failed — use Resend invitation",
    expired: (i) => `Invitation expired${i.expires_at ? ` ${fmtWhen(i.expires_at)}` : ""} — use Resend invitation`,
    active: (i) => `Setup completed${i.activated_at ? ` · ${fmtWhen(i.activated_at)}` : ""}`,
  };
  const inviteStatusText = (u) => {
    const i = u.invitation;
    if (!i || !i.state || !INVITE_LABEL[i.state]) return null;
    return INVITE_LABEL[i.state](i);
  };

  // live delivery state: re-poll every 4s only while an invite is still in flight
  useEffect(() => {
    const inflight = users.some((u) => u.invitation?.state === "pending");
    if (!inflight) return;
    const t = setTimeout(onChanged, 4000);
    return () => clearTimeout(t);
  }, [users, onChanged]);

  const handleSaveScope = async (e) => {
    e.preventDefault();
    if (!scopeTarget) return;
    setSavingScope(true);
    try {
      await api.patch(`/dealers/${dealerId}/users/${scopeTarget.id}/scope`, {
        dealer_role: scopeForm.dealer_role,
        ...(scopeForm.dealer_role !== "DEALER_ADMIN" ? { location_id: scopeForm.location_id } : {}),
        job_title: scopeForm.job_title.trim(),
      });
      toast.success(`Access updated for ${scopeTarget.email}`);
      setScopeTarget(null);
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingScope(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddingUser(true);
    try {
      const payload = {
        name: userForm.name,
        email: userForm.email,
        ...(userForm.password ? { password: userForm.password } : {}),
        ...(userForm.job_title.trim() ? { job_title: userForm.job_title.trim() } : {}),
        dealer_role: formRole,
        ...(formRole !== "DEALER_ADMIN" ? { location_id: userForm.location_id } : {}),
      };
      const { data } = await api.post(`/dealers/${dealerId}/users`, payload);
      toast.success(`User ${data.email} created — invitation queued (link valid 72 hours once delivered)`);
      setUserForm({ name: "", email: "", password: "", dealer_role: "DEALER_ADMIN", job_title: "", location_id: "" });
      setDialogOpen(false);
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAddingUser(false);
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      await api.post(`/dealers/${dealerId}/users/${userId}/reset-password`, {});
      toast.success("Reset link emailed to the user — valid 2 hours");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleToggleUser = async (userId, active, continuityPayload = null) => {
    try {
      await api.patch(`/dealers/${dealerId}/users/${userId}/status`, {
        active,
        ...(continuityPayload ? { continuity: continuityPayload } : {}),
      });
      toast.success(active ? "User activated" : "User deactivated");
      setContinuity(null);
      onChanged();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (!active && detail?.code === "project_continuity_required") {
        // Phase 0: the departing user owns active projects — require a decision
        setContinuity({ userId, detail });
        setContinuityChoice("UNASSIGNED");
        return;
      }
      toast.error(formatApiErrorDetail(detail));
    }
  };

  const handleArchive = async (userId, archive) => {
    try {
      await api.post(`/dealers/${dealerId}/users/${userId}/${archive ? "archive" : "reactivate"}`);
      toast.success(archive ? "User archived" : "User restored");
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleResendInvite = async () => {
    if (!resendTarget) return;
    setResending(true);
    try {
      await api.post(`/dealers/${dealerId}/users/${resendTarget.id}/resend-invite`);
      toast.success(`Invitation queued for ${resendTarget.email} — delivery status updates below`);
      setResendTarget(null);
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setResending(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm text-warmgrey">Authorized users for this dealer account.</p>
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-warmgrey">
            <Switch
              checked={showArchived}
              onCheckedChange={onToggleArchived}
              data-testid="show-archived-users-toggle"
            />
            Show archived
          </label>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-user-button" className="h-10 bg-terracotta text-white hover:bg-terracotta-hover">
                <Plus className="mr-2 h-4 w-4" /> Add User
              </Button>
            </DialogTrigger>
            <DialogContent className="border-border bg-white sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl font-medium text-charcoal">Add dealer user</DialogTitle>
                <DialogDescription className="text-sm text-warmgrey">The user must set a new password on first login.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddUser} className="mt-2 space-y-5" data-testid="add-user-form">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Full name *</Label>
                  <Input required data-testid="user-name-input" value={userForm.name} onChange={(e) => setUserForm((f) => ({ ...f, name: e.target.value }))} className="h-11 bg-white focus-visible:ring-terracotta" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Email *</Label>
                  <Input required type="email" data-testid="user-email-input" value={userForm.email} onChange={(e) => setUserForm((f) => ({ ...f, email: e.target.value }))} className="h-11 bg-white focus-visible:ring-terracotta" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">
                    Password <span className="normal-case text-muted-foreground">(leave blank to auto-generate)</span>
                  </Label>
                  <Input type="text" data-testid="user-password-input" value={userForm.password} onChange={(e) => setUserForm((f) => ({ ...f, password: e.target.value }))} className="h-11 bg-white focus-visible:ring-terracotta" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">
                    Job title <span className="normal-case text-muted-foreground">(optional — descriptive only)</span>
                  </Label>
                  <Input data-testid="user-job-title-input" value={userForm.job_title} onChange={(e) => setUserForm((f) => ({ ...f, job_title: e.target.value }))} placeholder="e.g. Showroom Director" className="h-11 bg-white focus-visible:ring-terracotta" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Role</Label>
                  <Select value={formRole} onValueChange={(v) => setUserForm((f) => ({ ...f, dealer_role: v, location_id: "" }))} disabled={!hasUsableUsers}>
                    <SelectTrigger data-testid="user-role-select" className="h-11 bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="DEALER_ADMIN">Dealer Admin — all locations, administrative authority</SelectItem>
                      <SelectItem value="LOCATION_MANAGER">Location Manager — one assigned location</SelectItem>
                      <SelectItem value="SALES_DESIGNER">Sales / Designer — one assigned location, day-to-day selling</SelectItem>
                    </SelectContent>
                  </Select>
                  {!hasUsableUsers && (
                    <p className="text-xs text-muted-foreground" data-testid="first-user-admin-note">The first user of a dealer is always the Dealer Admin.</p>
                  )}
                </div>
                {formRole !== "DEALER_ADMIN" && (
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Assigned location *</Label>
                    <Select value={userForm.location_id} onValueChange={(v) => setUserForm((f) => ({ ...f, location_id: v }))}>
                      <SelectTrigger data-testid="user-location-select" className="h-11 bg-white"><SelectValue placeholder="Select location" /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {locations.map((l) => (
                          <SelectItem key={l.id} value={l.id}>{l.label || l.type} — {l.address?.city}{l.address?.state ? `, ${l.address.state}` : ""}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <Button type="submit" disabled={addingUser || (formRole !== "DEALER_ADMIN" && !userForm.location_id)} data-testid="add-user-submit" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
                  {addingUser ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create user"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="mt-6 space-y-3">
        {users.length === 0 ? (
          <Card className="border-border bg-white">
            <CardContent className="p-8 text-center text-sm text-muted-foreground" data-testid="users-empty">
              {showArchived ? "No archived users." : "No users yet. Add the first authorized user for this dealer."}
            </CardContent>
          </Card>
        ) : (
          users.map((u) => (
            <Card key={u.id} className="border-border bg-white" data-testid={`user-row-${u.email}`}>
              <CardContent className="flex flex-wrap items-center justify-between gap-4 p-6">
                <div>
                  <p className="font-medium text-charcoal">{u.name}</p>
                  <p className="text-sm text-muted-foreground">{u.email}</p>
                  <span className="mt-1 inline-block badge-status badge-neutral" data-testid={`user-access-${u.email}`}>{accessLabel(u)}</span>
                  {!u.archived && inviteStatusText(u) && (
                    <span className="mt-1 block text-xs text-warmgrey" data-testid={`invite-status-${u.email}`}>
                      {inviteStatusText(u)}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  {u.archived ? (
                    <>
                      <span className="badge-status badge-neutral" data-testid={`user-archived-badge-${u.email}`}>archived</span>
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid={`reactivate-user-${u.email}`}
                        onClick={() => handleArchive(u.id, false)}
                        className="border-limestone text-charcoal hover:bg-sand"
                      >
                        <ArchiveRestore className="mr-1 h-3.5 w-3.5" /> Restore
                      </Button>
                    </>
                  ) : (
                    <>
                      <span className={`badge-status ${LIFECYCLE_CLS[lifecycle(u)]}`} data-testid={`user-lifecycle-${u.email}`}>
                        {LIFECYCLE_LABEL[lifecycle(u)]}
                      </span>
                      {u.active && (
                        <Button
                          variant={u.force_password_change ? "default" : "outline"}
                          size="sm"
                          data-testid={`resend-invite-${u.email}`}
                          onClick={() => setResendTarget(u)}
                          className={u.force_password_change ? "bg-terracotta text-white hover:bg-terracotta-hover" : "border-limestone text-charcoal hover:bg-sand"}
                        >
                          <Mail className="mr-1 h-3.5 w-3.5" /> Resend invitation
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid={`edit-access-${u.email}`}
                        onClick={() => {
                          setScopeTarget(u);
                          setScopeForm({
                            dealer_role: effectiveRole(u),
                            location_id: u.location_id || "",
                            job_title: u.job_title || "",
                          });
                        }}
                        className="border-limestone text-charcoal hover:bg-sand"
                      >
                        Edit access
                      </Button>
                      {!u.force_password_change && (
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid={`reset-password-${u.email}`}
                          onClick={() => handleResetPassword(u.id)}
                          className="border-limestone text-charcoal hover:bg-sand"
                        >
                          <KeyRound className="mr-1 h-3.5 w-3.5" /> Reset password
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid={`toggle-user-${u.email}`}
                        onClick={() => handleToggleUser(u.id, !u.active)}
                        className="border-limestone text-charcoal hover:bg-sand"
                      >
                        {u.active ? "Deactivate" : "Activate"}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid={`archive-user-${u.email}`}
                        onClick={() => handleArchive(u.id, true)}
                        className="border-limestone text-status-sold hover:bg-sand"
                      >
                        <Archive className="mr-1 h-3.5 w-3.5" /> Archive
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog open={!!scopeTarget} onOpenChange={(open) => !open && setScopeTarget(null)}>
        <DialogContent className="border-border bg-white sm:max-w-md" data-testid="edit-access-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium text-charcoal">Edit access</DialogTitle>
            <DialogDescription className="text-sm text-warmgrey">
              Dealer Admins have dealer-wide administrative authority. Location Managers and Sales/Designers work within one assigned location. Job title is descriptive only.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveScope} className="mt-2 space-y-5" data-testid="edit-access-form">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Role</Label>
              <Select value={scopeForm.dealer_role} onValueChange={(v) => setScopeForm((f) => ({ ...f, dealer_role: v, location_id: "" }))}>
                <SelectTrigger data-testid="edit-access-role-select" className="h-11 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="DEALER_ADMIN">Dealer Admin — all locations, administrative authority</SelectItem>
                  <SelectItem value="LOCATION_MANAGER">Location Manager — one assigned location</SelectItem>
                  <SelectItem value="SALES_DESIGNER">Sales / Designer — one assigned location, day-to-day selling</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">
                Job title <span className="normal-case text-muted-foreground">(optional — descriptive only)</span>
              </Label>
              <Input data-testid="edit-access-job-title-input" value={scopeForm.job_title} onChange={(e) => setScopeForm((f) => ({ ...f, job_title: e.target.value }))} className="h-11 bg-white focus-visible:ring-terracotta" />
            </div>
            {scopeForm.dealer_role !== "DEALER_ADMIN" && (
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Assigned location *</Label>
                <Select value={scopeForm.location_id} onValueChange={(v) => setScopeForm((f) => ({ ...f, location_id: v }))}>
                  <SelectTrigger data-testid="edit-access-location-select" className="h-11 bg-white"><SelectValue placeholder="Select location" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {locations.map((l) => (
                      <SelectItem key={l.id} value={l.id}>{l.label || l.type} — {l.address?.city}{l.address?.state ? `, ${l.address.state}` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button type="submit" disabled={savingScope || (scopeForm.dealer_role !== "DEALER_ADMIN" && !scopeForm.location_id)} data-testid="edit-access-submit" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
              {savingScope ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save access"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resendTarget} onOpenChange={(open) => !open && setResendTarget(null)}>
        <DialogContent className="border-border bg-white sm:max-w-md" data-testid="resend-invite-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium text-charcoal">Resend invitation</DialogTitle>
            <DialogDescription className="text-sm text-warmgrey" data-testid="resend-invite-copy">
              Send a new sign-in invitation to {resendTarget?.email}? Their previous password will no longer
              work and they will be required to create a new password after signing in.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 flex justify-end gap-3">
            <Button
              variant="outline"
              data-testid="resend-invite-cancel"
              onClick={() => setResendTarget(null)}
              className="border-limestone text-charcoal hover:bg-sand"
            >
              Cancel
            </Button>
            <Button
              data-testid="resend-invite-confirm"
              disabled={resending}
              onClick={handleResendInvite}
              className="bg-terracotta text-white hover:bg-terracotta-hover"
            >
              {resending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Invitation"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!continuity} onOpenChange={(open) => !open && setContinuity(null)}>
        <DialogContent className="border-border bg-white sm:max-w-md" data-testid="continuity-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium text-charcoal">Project continuity</DialogTitle>
            <DialogDescription className="text-sm text-warmgrey" data-testid="continuity-copy">
              {continuity?.detail?.message} Projects are never deleted — notes and history keep the original attribution.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <Select value={continuityChoice} onValueChange={setContinuityChoice}>
              <SelectTrigger data-testid="continuity-target-select" className="h-11 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="UNASSIGNED" data-testid="continuity-option-unassigned">Leave unassigned — stays at the original location</SelectItem>
                {(continuity?.detail?.eligible_users || []).map((u) => (
                  <SelectItem key={u.id} value={u.id} data-testid={`continuity-option-${u.id}`}>Reassign to {u.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              data-testid="continuity-confirm"
              onClick={() =>
                handleToggleUser(continuity.userId, false,
                  continuityChoice === "UNASSIGNED" ? { action: "unassign" } : { action: "reassign", to_user_id: continuityChoice })
              }
              className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover"
            >
              Deactivate user
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
